import AGMessage from 'components/AGMessage/AGMessage';
import AGTAbleWrapper from 'components/AGTable/AGTableWrapper';
import Loader from 'components/Loader/Loader';
import MinimumGuaranteeBox from 'components/MinimumGuaranteeBox/MinimumGuaranteeBox';
import React, { useEffect } from 'react';
import { Popup } from 'semantic-ui-react';
import { DailyReportInterface } from 'types';
import { getMonthYearLabel, MonthYear } from 'utils/monthYear';
import styles from './Reports.module.css';

type ReportsModuleProps = {
    selectedDailyReports: DailyReportInterface | null;
    loading: boolean;
    filters: {
        monthYear: MonthYear['value'];
    };
};

function ReportTablePopup({ title, content }: { title: string; content: string }) {
    return <Popup trigger={<th>{title}</th>} content={{ content }} on="hover" pinned inverted position="top left" />;
}

function ReportsModule({ selectedDailyReports, filters: { monthYear }, loading }: ReportsModuleProps) {
    const [showTaxesColumn, setShowTaxesColumn] = React.useState(false);
    const [showPaymentProcessingFeeColumn, setShowPaymentProcessingFeeColumn] = React.useState(false);
    const total = selectedDailyReports && selectedDailyReports.total;

    useEffect(() => {
        if (!selectedDailyReports) {
            setShowTaxesColumn(false);
            setShowPaymentProcessingFeeColumn(false);
            return;
        }
        const [month, year] = monthYear.split(':');
        if (month && year) {
            // We only want to show the taxes column and payment processing fee column for reports after May 1, 2023 12 am UTC
            const cutOffDate = new Date('2023-05-01T00:00:00Z'); // May 1, 12 am UTC
            const selectedDate = new Date(`${year}-${month}-01T00:00:00Z`);

            if (selectedDailyReports.total.tax_amount > 0 && selectedDate >= cutOffDate) {
                // If the total taxes amount is greater than 0, and the selected date is
                // after the cut off date, show the taxes column.
                setShowTaxesColumn(true);
            }
            if (selectedDailyReports.total.client_payment_processing_fee > 0 && selectedDate >= cutOffDate) {
                // If the total payment processing fee amount is greater than 0, and the selected date is
                // after the cut off date, show the payment processing fee column.
                setShowPaymentProcessingFeeColumn(true);
            }
        }
    }, [monthYear, selectedDailyReports]);

    const organizationHasAdjustments = selectedDailyReports?.organization_has_adjustments;
    const minimumGuarantee = selectedDailyReports?.minimum_guarantee;

    return (
        <>
            <AGTAbleWrapper className={styles.reportsTable}>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Count of Rentals</th>
                        <ReportTablePopup
                            title="Gross Revenue"
                            content="This is the topline booking value before refunds and AirGarage commission are accounted for."
                        />

                        <ReportTablePopup
                            title="Count of Refunds"
                            content="Refunds are issued to maintain a high bar for quality service. Refunds can be given for customer support issues or cancelled rentals."
                        />
                        <th>Refund Amount</th>
                        {showTaxesColumn && (
                            <ReportTablePopup
                                title="Tax Amount"
                                content="This is the value of taxes collected from Adjusted Revenue."
                            />
                        )}
                        {showPaymentProcessingFeeColumn && (
                            <ReportTablePopup
                                title="Payment Processing Fee"
                                content="This is the value of payment processing fees collected from Adjusted Revenue."
                            />
                        )}
                        <ReportTablePopup
                            title="Adjusted Revenue"
                            content="This value is gross revenue minus the refunded amount."
                        />
                        <ReportTablePopup
                            title="AirGarage Commission"
                            content="This is the value of AirGarage's take from Adjusted Revenue minus Tax Amount, which includes payment processing."
                        />
                        <ReportTablePopup
                            title="Net Revenue"
                            content="This is the value of your take from Adjusted Revenue."
                        />
                    </tr>
                </thead>

                {loading ? (
                    <Loader />
                ) : !selectedDailyReports ? (
                    <tr>
                        <td colSpan={8} style={{ textAlign: 'left' }}>
                            Reports will show up here
                        </td>
                    </tr>
                ) : (
                    <>
                        {selectedDailyReports.daily_reports.map((report) => {
                            return (
                                <tbody key={`report_${report.date}`}>
                                    <tr>
                                        <td>{report.date}</td>
                                        <td>{report.count_of_rentals}</td>
                                        <td>{report.gross_revenue_formatted}</td>
                                        <td>{report.count_of_refunds}</td>
                                        <td>{report.refund_amount_formatted}</td>
                                        {showTaxesColumn && <td>{report.tax_amount_formatted}</td>}
                                        {showPaymentProcessingFeeColumn && (
                                            <td>{report.client_payment_processing_fee_formatted}</td>
                                        )}
                                        <td>{report.adjusted_revenue_formatted}</td>
                                        <td>{report.contribution_amount_exclude_tax_formatted}</td>
                                        <td>{report.net_revenue_formatted}</td>
                                    </tr>
                                </tbody>
                            );
                        })}
                        {total && (
                            <tfoot>
                                <tr>
                                    <td>{total.date}</td>
                                    <td>{total.count_of_rentals}</td>
                                    <td>{total.gross_revenue_formatted}</td>
                                    <td>{total.count_of_refunds}</td>
                                    <td>{total.refund_amount_formatted}</td>
                                    {showTaxesColumn && <td>{total.tax_amount_formatted}</td>}
                                    {showPaymentProcessingFeeColumn && (
                                        <td>{total.client_payment_processing_fee_formatted}</td>
                                    )}
                                    <td>{total.adjusted_revenue_formatted}</td>
                                    <td>{total.contribution_amount_exclude_tax_formatted}</td>
                                    <td>{total.net_revenue_formatted}</td>
                                </tr>
                            </tfoot>
                        )}
                    </>
                )}
            </AGTAbleWrapper>
            {!loading && (!!organizationHasAdjustments || !!minimumGuarantee) && (
                <div className={styles.infoBoxes}>
                    {!!organizationHasAdjustments && (
                        <AGMessage color="info" title="Adjustments" className={styles.adjustmentsBox}>
                            An adjustment is a month-end correction that trues up your revenue split when small
                            differences accumulate during the month from mid-month rate changes, refunds, or
                            per-transaction rounding, so your payout always matches your agreement.
                        </AGMessage>
                    )}
                    {!!minimumGuarantee && (
                        <MinimumGuaranteeBox
                            netRevenue={minimumGuarantee.net_revenue}
                            minimumGuaranteeAmount={minimumGuarantee.minimum_guarantee_amount}
                            monthLabel={getMonthYearLabel(monthYear)}
                        />
                    )}
                </div>
            )}
        </>
    );
}

export default ReportsModule;
